import { motion, useReducedMotion } from "framer-motion";

/**
 * AIStrategy — "Signal from Noise"
 * Top: a jagged, noisy waveform (raw AI hype / experiments).
 * Bottom: a clean, structured sine wave (governed, commercial AI value).
 * A horizontal "filter line" sits between the two.
 * Reflects: separating genuine AI value from noise, portfolio governance.
 */
export default function SignalFromNoise({ className = "" }) {
  const reduce = useReducedMotion();

  // Two paths sampled from math functions
  const noise = "M 20 90 L 40 60 L 55 110 L 75 40 L 95 100 L 110 55 L 135 115 L 155 45 L 175 95 L 195 50 L 215 110 L 235 40 L 255 100 L 275 60 L 300 105 L 320 55 L 345 100 L 370 60 L 385 95";
  const signal = "M 20 240 C 60 210, 100 270, 140 240 S 220 210, 260 240 S 340 270, 385 240";

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 400 320" className="h-full w-full" role="presentation">
        <defs>
          <linearGradient id="sig-noise" x1="0" x2="1">
            <stop offset="0%" stopColor="#7DB4FF" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#7DB4FF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#7DB4FF" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="sig-signal" x1="0" x2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#3B82F6" stopOpacity="1" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Top label */}
        <text x="20" y="35" fill="#94A3B8" fontSize="9" letterSpacing="2.5" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          NOISE  ·  EXPERIMENTS  ·  HYPE
        </text>

        {/* Noisy waveform — jitters in opacity */}
        <motion.path
          d={noise}
          fill="none"
          stroke="url(#sig-noise)"
          strokeWidth="1"
          strokeLinejoin="miter"
          initial={{ opacity: 0.45 }}
          animate={reduce ? { opacity: 0.6 } : { opacity: [0.35, 0.8, 0.45, 0.7, 0.4] }}
          transition={reduce ? { duration: 0 } : { duration: 3.2, repeat: Infinity, ease: "linear" }}
        />

        {/* Filter row — the "governance line" between chaos and signal */}
        <line x1="10" y1="165" x2="390" y2="165" stroke="#334155" strokeDasharray="2 6" />
        <text x="200" y="158" textAnchor="middle" fill="#93C5FD" fontSize="9" letterSpacing="3" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          GOVERNED PORTFOLIO
        </text>
        {/* Filter arrows */}
        {[80, 160, 240, 320].map((x) => (
          <g key={x}>
            <motion.path
              d={`M ${x} 130 L ${x} 200`}
              stroke="#7DB4FF"
              strokeOpacity="0.4"
              strokeWidth="1"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={reduce ? { pathLength: 1, opacity: 0.5 } : { pathLength: [0, 1, 1], opacity: [0, 0.7, 0] }}
              transition={reduce ? { duration: 0 } : { duration: 3, delay: (x / 400) * 1.2, repeat: Infinity, ease: "easeOut" }}
            />
            <polygon points={`${x - 3},198 ${x + 3},198 ${x},204`} fill="#7DB4FF" opacity="0.6" />
          </g>
        ))}

        {/* Bottom label */}
        <text x="20" y="290" fill="#94A3B8" fontSize="9" letterSpacing="2.5" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          SIGNAL  ·  BUSINESS VALUE
        </text>

        {/* Clean signal wave */}
        <motion.path
          d={signal}
          fill="none"
          stroke="url(#sig-signal)"
          strokeWidth="2"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={reduce ? { duration: 0 } : { duration: 2.2, ease: "easeInOut" }}
        />
        {/* Rolling highlight along the signal */}
        {!reduce && (
          <circle r="3.5" fill="#EFF6FF">
            <animateMotion dur="5s" repeatCount="indefinite" path={signal} />
          </circle>
        )}
      </svg>
    </div>
  );
}
